import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import {
  Table,
  Space,
  Typography,
  Spin,
  Empty,
  Button,
  Tooltip,
  message,
  Image,
  Modal,
  Tag,
  BackTop,
  Menu,
  DatePicker,
  Radio,
} from "antd";
import {
  FileImageOutlined,
  FileOutlined,
  DownloadOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  FilterOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import queryString from "query-string";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { useDynamicFilter } from "./filters";
import FiltersComponent from "./FiltersComponent";
import ParseLogo from "../shared/images/parse.png";

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "-";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString() + " " + date.toLocaleTimeString();
};

// Get icon based on mime type
const getFileIcon = (mimeType, size = 24) => {
  const iconProps = { style: { fontSize: size } };
  
  if (mimeType && mimeType.startsWith("image/")) {
    return <FileImageOutlined {...iconProps} style={{ ...iconProps.style, color: "#52c41a" }} />;
  }
  
  return <FileOutlined {...iconProps} />;
};

// Custom filter dropdown component for Organizzazione column
const OrganizzazioneFilterDropdown = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  handleFilterChange,
  activeFilters,
  organizationNames,
}) => {
  const handleMenuClick = (value) => {
    // Add a filter to the top filters
    handleFilterChange([
      ...activeFilters,
      {
        id: Date.now(),
        column: "org_pointer",
        columnName: "org_pointer",
        value: { have: { objectId: { equalTo: value.objectId } } },
        displayValue: value.nome,
        operator: "equalTo",
        logicalOperator: "AND",
      },
    ]);

    // Close the dropdown
    confirm();
  };

  return (
    <div style={{ padding: 8, maxHeight: 300, overflow: "auto" }}>
      <Menu>
        {organizationNames.map((org) => (
          <Menu.Item
            key={org.objectId}
            onClick={() => handleMenuClick(org)}
          >
            {org.nome}
          </Menu.Item>
        ))}
      </Menu>
    </div>
  );
};

// Custom filter dropdown for createdAt column
const CreatedAtFilterDropdown = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  handleFilterChange,
  activeFilters,
}) => {
  const [filterType, setFilterType] = useState("quick");
  const [selectedDates, setSelectedDates] = useState([]);

  const handleQuickFilter = (type) => {
    let startDate, endDate;
    const now = moment();
    
    switch (type) {
      case "today":
        startDate = now.clone().startOf("day");
        endDate = now.clone().endOf("day");
        break;
      case "thisWeek":
        startDate = now.clone().startOf("week");
        endDate = now.clone().endOf("week");
        break;
      case "thisMonth":
        startDate = now.clone().startOf("month");
        endDate = now.clone().endOf("month");
        break;
      case "thisYear":
        startDate = now.clone().startOf("year");
        endDate = now.clone().endOf("year");
        break;
      default:
        return;
    }

    const displayValue = type === "today" ? "Oggi" :
                        type === "thisWeek" ? "Questa settimana" :
                        type === "thisMonth" ? "Questo mese" :
                        type === "thisYear" ? "Quest'anno" : "";

    handleFilterChange([
      ...activeFilters,
      {
        id: Date.now(),
        column: "createdAt",
        columnName: "createdAt",
        value: {
          greaterThanOrEqualTo: startDate.toISOString(),
          lessThanOrEqualTo: endDate.toISOString(),
        },
        displayValue: displayValue,
        operator: "range",
        logicalOperator: "AND",
      },
    ]);

    confirm();
  };

  const handleRangeFilter = () => {
    if (selectedDates && selectedDates.length === 2) {
      const startDate = selectedDates[0].startOf("day");
      const endDate = selectedDates[1].endOf("day");

      handleFilterChange([
        ...activeFilters,
        {
          id: Date.now(),
          column: "createdAt",
          columnName: "createdAt",
          value: {
            greaterThanOrEqualTo: startDate.toISOString(),
            lessThanOrEqualTo: endDate.toISOString(),
          },
          displayValue: `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`,
          operator: "range",
          logicalOperator: "AND",
        },
      ]);

      confirm();
    }
  };

  return (
    <div style={{ padding: 16, width: 300 }}>
      <Radio.Group
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="quick">Filtri rapidi</Radio.Button>
        <Radio.Button value="custom">Intervallo personalizzato</Radio.Button>
      </Radio.Group>

      {filterType === "quick" ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block onClick={() => handleQuickFilter("today")}>
            Oggi
          </Button>
          <Button block onClick={() => handleQuickFilter("thisWeek")}>
            Questa settimana
          </Button>
          <Button block onClick={() => handleQuickFilter("thisMonth")}>
            Questo mese
          </Button>
          <Button block onClick={() => handleQuickFilter("thisYear")}>
            Quest'anno
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }}>
          <RangePicker
            style={{ width: "100%" }}
            onChange={(dates) => setSelectedDates(dates)}
            format="DD/MM/YYYY"
          />
          <Button
            type="primary"
            block
            disabled={!selectedDates || selectedDates.length !== 2}
            onClick={handleRangeFilter}
          >
            Applica filtro
          </Button>
        </Space>
      )}
    </div>
  );
};

const Allegati = () => {
  const location = useLocation();
  const history = useHistory();
  const isInitialMount = useRef(true);
  const initialDataLoaded = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [organizationNames, setOrganizationNames] = useState([]);
  
  const { isProd } = useAppContext();

  // Use the dynamic filter hook
  const {
    activeFilters,
    fetchFilteredData,
    handleFilterChange,
    clearAll
  } = useDynamicFilter(
    setLoading,
    setItems,
    setHasNextPage,
    setTotalCount,
    setLoadingMore
  );

  // Define filterable columns
  const filterableColumns = [
    {
      key: "org_pointer",
      title: "org_pointer",
      type: "string",
    },
    {
      key: "nome",
      title: "nome",
      type: "string",
    },
    {
      key: "mimeType",
      title: "mimeType",
      type: "string",
      options: [
        { value: "image/jpeg", text: "JPEG" },
        { value: "image/png", text: "PNG" },
        { value: "image/gif", text: "GIF" },
        { value: "image/webp", text: "WebP" },
        { value: "application/pdf", text: "PDF" },
      ],
    },
    {
      key: "condiviso",
      title: "condiviso",
      type: "boolean",
    },
    {
      key: "provino",
      title: "provino",
      type: "boolean",
    },
    {
      key: "defunto_pointer",
      title: "defunto_pointer",
      type: "string",
    },
    {
      key: "office_pointer",
      title: "office_pointer",
      type: "pointer",
    },
    {
      key: "size_bytes",
      title: "size_bytes",
      type: "number",
    },
    {
      key: "isAiEdit",
      title: "isAiEdit",
      type: "boolean",
    },
    {
      key: "isRemovebg",
      title: "isRemovebg",
      type: "boolean",
    },
    {
      key: "isImageEnhancement",
      title: "isImageEnhancement",
      type: "boolean",
    },
    {
      key: "isExpand",
      title: "isExpand",
      type: "boolean",
    },
    {
      key: "isMagicEraser",
      title: "isMagicEraser",
      type: "boolean",
    },
    {
      key: "isChangeClothes",
      title: "isChangeClothes",
      type: "boolean",
    },
    {
      key: "isSmile",
      title: "isSmile",
      type: "boolean",
    },
    {
      key: "isColorizeBw",
      title: "isColorizeBw",
      type: "boolean",
    },
    {
      key: "isAutoColor",
      title: "isAutoColor",
      type: "boolean",
    },
    {
      key: "isBlurBg",
      title: "isBlurBg",
      type: "boolean",
    },
    {
      key: "createdAt",
      title: "createdAt",
      type: "date",
    },
    {
      key: "updatedAt",
      title: "updatedAt",
      type: "date",
    },
  ];

  // Extract unique organization names from items
  useEffect(() => {
    const uniqueOrgs = items
      .filter(item => item.org_pointer?.nome)
      .map(item => ({ objectId: item.org_pointer.objectId, nome: item.org_pointer.nome }))
      .filter((org, index, self) => 
        index === self.findIndex(o => o.objectId === org.objectId)
      )
      .sort((a, b) => a.nome.localeCompare(b.nome));
    setOrganizationNames(uniqueOrgs);
  }, [items]);

  // Load initial data from URL params
  useEffect(() => {
    if (isInitialMount.current && !initialDataLoaded.current) {
      const params = queryString.parse(location.search);
      let parsedFilters = [];
      
      if (params.filters) {
        try {
          parsedFilters = JSON.parse(params.filters);
        } catch (error) {
          console.error("Error parsing filters from URL:", error);
        }
      }
      
      initialDataLoaded.current = true;
      
      // Set filters state if present
      if (parsedFilters.length > 0) {
        handleFilterChange(parsedFilters);
      } else {
        // Fetch initial data without filters
        fetchFilteredData({ skip: 0, isRefresh: true });
      }
      
      isInitialMount.current = false;
    }
  }, [location.search, handleFilterChange, fetchFilteredData]);

  // Update URL when filters change
  useEffect(() => {
    const currentParams = queryString.parse(location.search);
    const newParams = {
      ...currentParams,
    };
    
    // Only add filters param if there are active filters
    if (activeFilters.length > 0) {
      newParams.filters = JSON.stringify(activeFilters);
    } else {
      // Remove filters param when empty
      delete newParams.filters;
    }

    const newSearch = queryString.stringify(newParams);
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    // Update URL without adding to browser history
    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  }, [activeFilters, location.pathname, location.search, history]);

  // Handle file preview
  const handlePreview = (item) => {
    if (item.mimeType && item.mimeType.startsWith("image/") && item.allegato?.url) {
      setPreviewImage(item.allegato.url);
      setPreviewTitle(item.nome || "Anteprima");
      setPreviewVisible(true);
    }
  };

  // Handle file actions
  const handleFileAction = (item, action) => {
    const fileUrl = item.allegato?.url;
    
    if (!fileUrl) {
      message.warning("URL file non disponibile");
      return;
    }
    
    if (action === "view") {
      if (item.mimeType && item.mimeType.startsWith("image/")) {
        handlePreview(item);
      } else {
        window.open(fileUrl, "_blank");
      }
    } else if (action === "download") {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = item.nome || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (action === "copy") {
      navigator.clipboard.writeText(fileUrl).then(() => {
        message.success("URL copiato negli appunti!");
      }).catch(() => {
        message.error("Impossibile copiare l'URL");
      });
    }
  };

  // Load more items for infinite scroll
  const handleLoadMore = () => {
    if (hasNextPage && !loadingMore) {
      fetchFilteredData({ skip: items.length });
    }
  };

  // Refresh the current view
  const handleRefresh = () => {
    fetchFilteredData({ skip: 0, isRefresh: true });
  };

  // Table columns
  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      render: (text, record) => {
        const fileUrl = record.allegato?.url;
        const isImage = record.mimeType && record.mimeType.startsWith("image/");
        
        return (
          <Space>
            {isImage && fileUrl ? (
              <div style={{
                width: 40,
                height: 40,
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                overflow: "hidden",
                background: "repeating-conic-gradient(#f0f0f0 0% 25%, transparent 0% 50%) 50% / 10px 10px",
                display: "inline-block",
              }}>
                <Image
                  width={40}
                  height={40}
                  src={fileUrl}
                  preview={false}
                  style={{ 
                    objectFit: "cover", 
                    cursor: "pointer",
                    display: "block",
                  }}
                  onClick={() => handlePreview(record)}
                />
              </div>
            ) : (
              getFileIcon(record.mimeType)
            )}
            <Text>{text || "Senza nome"}</Text>
          </Space>
        );
      },
    },
    {
      title: "Tipo",
      dataIndex: "mimeType",
      key: "mimeType",
      width: 150,
      render: (mimeType) => mimeType || "-",
    },
    {
      title: "Dimensione",
      dataIndex: "size_bytes",
      key: "size_bytes",
      width: 120,
      render: (size) => formatFileSize(size),
    },
    {
      title: "Organizzazione",
      dataIndex: ["org_pointer"],
      key: "org",
      width: 200,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <OrganizzazioneFilterDropdown
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          handleFilterChange={handleFilterChange}
          activeFilters={activeFilters}
          organizationNames={organizationNames}
        />
      ),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: activeFilters.some(f => f.column === "org_pointer") ? '#1890ff' : undefined }} />
      ),
      render: (org) => {
        if (!org || !org.nome) return "-";
        return (
          <Link to={`/orgs/${org.objectId}`}>
            {org.nome}
          </Link>
        );
      },
    },
    {
      title: "Defunto",
      dataIndex: ["defunto_pointer", "nome"],
      key: "defunto",
      width: 200,
      render: (nome, record) => {
        const cognome = record.defunto_pointer?.cognome;
        if (nome || cognome) {
          return `${nome || ""} ${cognome || ""}`.trim();
        }
        return "-";
      },
    },
    {
      title: "Condiviso",
      dataIndex: "condiviso",
      key: "condiviso",
      width: 100,
      render: (condiviso) => (
        <Tag color={condiviso ? "green" : "default"}>
          {condiviso ? "Sì" : "No"}
        </Tag>
      ),
    },
    {
      title: "Creato il",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <CreatedAtFilterDropdown
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          handleFilterChange={handleFilterChange}
          activeFilters={activeFilters}
        />
      ),
      filterIcon: (filtered) => (
        <CalendarOutlined style={{ color: activeFilters.some(f => f.column === "createdAt") ? '#1890ff' : undefined }} />
      ),
      render: (date) => formatDate(date),
    },
    {
      title: "Azioni",
      key: "actions",
      width: 180,
      render: (_, record) => {
        const parseDashboardUrl = `${
          config.parseDashboard[isProd ? "prod" : "dev"]
        }/browser/allegati?filters=[{"field":"objectId","constraint":"eq","compareTo":"${
          record.objectId
        }"}]`;
        
        return (
          <Space size="small">
            {record.allegato?.url && (
              <>
                <Tooltip title="Visualizza">
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleFileAction(record, "view")}
                  />
                </Tooltip>
                <Tooltip title="Copia URL">
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => handleFileAction(record, "copy")}
                  />
                </Tooltip>
                <Tooltip title="Scarica">
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleFileAction(record, "download")}
                  />
                </Tooltip>
              </>
            )}
            <Tooltip title="Apri in Parse Dashboard">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={parseDashboardUrl}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <img src={ParseLogo} width={16} alt="Parse Dashboard" />
              </a>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            <Space>
              <FileOutlined />
              Allegati
              {totalCount > 0 && (
                <Text type="secondary" style={{ fontSize: "16px", fontWeight: "normal" }}>
                  ({totalCount} totali)
                </Text>
              )}
            </Space>
          </Title>
          
          <Space>
            <FiltersComponent
              filterableColumns={filterableColumns}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearAll={clearAll}
              hideFilterTags={true}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              Aggiorna
            </Button>
          </Space>
        </div>

        <FiltersComponent
          filterableColumns={filterableColumns}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAll}
          onlyFilterTags={true}
        />
      </div>

      {items.length === 0 ? (
        <Empty
          description="Nessun allegato trovato"
          style={{ marginTop: "50px" }}
        />
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          next={handleLoadMore}
          hasMore={hasNextPage}
          loader={
            <div style={{ textAlign: "center", margin: "20px 0" }}>
              <Spin size="large" />
            </div>
          }
          endMessage={
            items.length > 0 && (
              <div style={{ textAlign: "center", margin: "20px 0", color: "#999" }}>
                Non ci sono altri allegati da caricare
              </div>
            )
          }
        >
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(record) => record.objectId}
            pagination={false}
            size="middle"
            scroll={{ x: 1200 }}
          />
        </InfiniteScroll>
      )}

      <BackTop />

      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          padding: "20px",
        }}
      >
        <div style={{ 
          textAlign: "center",
          padding: "20px",
          background: "repeating-conic-gradient(#f9f9f9 0% 25%, #ffffff 0% 50%) 50% / 20px 20px",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}>
          <Image
            src={previewImage}
            style={{ 
              maxWidth: "100%",
              maxHeight: "calc(100vh - 350px)",
              objectFit: "contain",
            }}
            preview={{
              visible: false,
              mask: null,
            }}
          />
        </div>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const link = document.createElement("a");
                link.href = previewImage;
                link.download = previewTitle;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Scarica
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(previewImage).then(() => {
                  message.success("URL copiato negli appunti!");
                }).catch(() => {
                  message.error("Impossibile copiare l'URL");
                });
              }}
            >
              Copia URL
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(previewImage, "_blank")}
            >
              Apri in nuova scheda
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default Allegati;